import { useState } from 'react'
import {
    formatEmailCode,
    requestEmailLink,
    requestEmailLogin,
    verifyEmailLink,
    verifyEmailLogin,
} from 'shared/api/emailAuth'
import { notifySessionChanged } from 'shared/api/sessionAuth'
import { Button } from 'shared/ui/Button'
import { Text, TextAlign, TextWeight } from 'shared/ui/Text/Text'
import styles from './EmailAuthFlow.module.scss'

const errorText = (error) => {
    switch (error?.code) {
        case 'invalid_email':
            return 'Проверь адрес почты.'
        case 'invalid_email_code':
            return 'Код неверный или уже истёк.'
        case 'email_taken':
            return 'Эта почта уже привязана к другому профилю.'
        case 'email_rate_limited':
            return 'Новый код можно запросить через минуту.'
        case 'email_auth_unavailable':
            return 'Вход по почте пока недоступен.'
        default:
            return 'Не получилось выполнить запрос. Попробуй ещё раз.'
    }
}

export const EmailAuthFlow = ({ mode, user, onBack, onComplete }) => {
    const [step, setStep] = useState('email')
    const [email, setEmail] = useState('')
    const [code, setCode] = useState('')
    const [status, setStatus] = useState('idle')
    const [error, setError] = useState('')

    const submitEmail = async (event) => {
        event.preventDefault()
        setStatus('working')
        setError('')
        try {
            if (mode === 'link') {
                await requestEmailLink(user, email)
            } else {
                await requestEmailLogin(email)
            }
            setStep('code')
        } catch (requestError) {
            setError(errorText(requestError))
        } finally {
            setStatus('idle')
        }
    }

    const submitCode = async (event) => {
        event.preventDefault()
        setStatus('working')
        setError('')
        try {
            if (mode === 'link') {
                const result = await verifyEmailLink(user, email, code)
                onComplete?.(result)
                return
            }

            const result = await verifyEmailLogin(email, code)
            notifySessionChanged()
            onComplete?.(result)
        } catch (requestError) {
            setError(errorText(requestError))
        } finally {
            setStatus('idle')
        }
    }

    return (
        <div className={styles.flow}>
            <Text
                align={TextAlign.LEFT}
                className={styles.title}
                weight={TextWeight.BOLD}
            >
                {mode === 'link' ? 'Сохранить аккаунт' : 'Войти в MineCards'}
            </Text>
            <Text align={TextAlign.LEFT} className={styles.description}>
                {step === 'email'
                    ? mode === 'link'
                        ? 'Привяжи почту, чтобы восстановить этот профиль в другом браузере или PWA.'
                        : 'Введи почту, которую привязывал к своему профилю.'
                    : `Мы отправили шестизначный код на ${email.trim().toLowerCase()}.`}
            </Text>

            <form
                className={styles.form}
                onSubmit={step === 'email' ? submitEmail : submitCode}
            >
                {step === 'email' ? (
                    <label className={styles.label}>
                        Почта
                        <input
                            autoComplete="email"
                            autoFocus
                            className={styles.input}
                            inputMode="email"
                            onChange={(event) => setEmail(event.target.value)}
                            placeholder="name@example.com"
                            type="email"
                            value={email}
                        />
                    </label>
                ) : (
                    <label className={styles.label}>
                        Код из письма
                        <input
                            autoComplete="one-time-code"
                            autoFocus
                            className={`${styles.input} ${styles.codeInput}`}
                            inputMode="numeric"
                            maxLength={7}
                            onChange={(event) =>
                                setCode(formatEmailCode(event.target.value))
                            }
                            placeholder="000 000"
                            type="text"
                            value={code}
                        />
                    </label>
                )}

                {error && (
                    <p className={styles.error} role="alert">
                        {error}
                    </p>
                )}

                <Button
                    className={styles.submit}
                    disabled={status === 'working'}
                    text={
                        status === 'working'
                            ? 'Подожди…'
                            : step === 'email'
                              ? 'Получить код'
                              : mode === 'link'
                                ? 'Сохранить аккаунт'
                                : 'Войти'
                    }
                />
                {step === 'email' && (
                    <p className={styles.legalNotice}>
                        Продолжая, ты принимаешь{' '}
                        <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Пользовательское соглашение
                        </a>{' '}
                        и подтверждаешь, что ознакомился с{' '}
                        <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Политикой конфиденциальности
                        </a>
                        .
                    </p>
                )}
            </form>

            <div className={styles.secondaryActions}>
                {step === 'code' && (
                    <button
                        className={styles.textButton}
                        onClick={() => {
                            setStep('email')
                            setCode('')
                            setError('')
                        }}
                        type="button"
                    >
                        Изменить почту
                    </button>
                )}
                {onBack && (
                    <button
                        className={styles.textButton}
                        onClick={onBack}
                        type="button"
                    >
                        Назад
                    </button>
                )}
            </div>
        </div>
    )
}
